import { Accessor, createContext, createMemo, ParentProps, useContext } from 'solid-js'
import { createStore, SetStoreFunction } from 'solid-js/store'
import { ReportGroup, ReportInfoCard } from 'st-app/api'
import { debounce } from "common"
import { reportBrowser as api } from 'st-app/api'
import { toast } from 'common'

const Context = createContext<ReportBrowserContext>()

export type ViewMode = 'list' | 'tile'

class ReportBrowserState {
    searchResults: ReportInfoCard[] = []
    groups: ReportGroup[] = []
    viewMode: ViewMode = 'tile'
    searchStr: string = ''
    selectedGroupId?: string
    selectedReportId?: string
    isEditing = false 
    isCreating = false 
    isInCarousel = false 
    carouselIndex = 0 
    canEdit = false
    canAdd = false 
    canPublish = false
    canAccessMyReports = false
    isPublishDialogOpen = false
    isPublishing = false 
    isCopyDialogOpen = false 
    isCopying = false 
    isThumbnailDialogOpen = false
    isUpdatingThumbnail = false 
    isDeleteDialogOpen = false 
    isDeleting = false 
    isLoadingGroups = false
    reportBrowserScrollTop = 0
}

export class ReportBrowserContext {
    state: ReportBrowserState
    private setState: SetStoreFunction<ReportBrowserState>
    allReports: Accessor<ReportInfoCard[]>
    selectedGroup: Accessor<ReportGroup | undefined>
    selectedReport: Accessor<ReportInfoCard | undefined>
    reports: Accessor<ReportInfoCard[]>
    shouldShowReports: Accessor<boolean>

    constructor() {
        [this.state, this.setState] = createStore(new ReportBrowserState())
        this.allReports = createMemo(() => this.state.groups.flatMap(g => g.reports))
        this.selectedGroup = createMemo(() => this.state.groups.find(g => g.description === this.state.selectedGroupId))
        this.selectedReport = createMemo(() => {
            const reportId = this.state.selectedReportId
            return this.allReports().find(r => r.reportGU === reportId)
        });
        
        this.reports = createMemo(() => {
            const searchStr = this.state.searchStr
            const selected = this.selectedGroup()
            if (searchStr) return this.state.searchResults ?? []
            if (selected) return selected.reports
            return []
        })

        this.shouldShowReports = createMemo(() => !!(this.state.searchStr || this.selectedGroup()))
    }



    async refresh() {
        this.setReportGroupLoading(true)
        try {
            const groupResults = await api.LoadReports()
            this.setGroups(groupResults)
        } catch (e) {
            toast.notify('error', 'An error ocurred while refreshing.')
        } finally {
            this.setReportGroupLoading(false)
        }
    }

    setGroups(value: ReportGroup[]) {
        this.setState('groups', value)
    }

    toggleEditMode(v?: boolean) {
        this.setState('isEditing', (current) => v ?? !current)
    }

    toggleCreateMode(v?: boolean) {
        this.setState('isCreating', (current) => v ?? !current)
    }

    setSearchStr(s: string) {
        this.setState('searchStr', s)
        this.search(s)
    }

    setSelectedGroup(g: ReportGroup | null) {
        this.setState('selectedGroupId', g?.description)
    }

    setViewMode(v: ViewMode) {
        this.toggleCarousel(false)
        this.setState('viewMode', v)
    }

    toggleCarousel(v?: boolean) {
        if (this.selectedGroup()) {
            this.setState('carouselIndex', 0)
            this.setState('isInCarousel', (e) => v ?? !e)
        }
    }

    carouselForward() {
        if (this.selectedGroup()) {
            if (this.state.carouselIndex < this.selectedGroup()!.reports.length - 1) {
                this.setState('carouselIndex', this.state.carouselIndex + 1)
            }
        }
    }

    carouselBackward() {
        if (this.selectedGroup()) {
            if (this.state.carouselIndex > 0) {
                this.setState('carouselIndex', this.state.carouselIndex - 1)
            }
        }
    }

    updateGroup(g: ReportGroup, update: Partial<ReportGroup>) {
        this.setState('groups', (group) => group.reportGroupDefGU === g.reportGroupDefGU, { ...g, ...update })
    }

    updateReport(r: ReportInfoCard, update: Partial<ReportInfoCard>) {
        if (this.state.searchResults) {

        }

        this.setState(
            'groups',
            (grp) => grp.reports.some(rpt => rpt.reportGU === r.reportGU),
            'reports',
            (rpt) => rpt.reportGU === r.reportGU,
            () => ({ ...r, ...update })
        )
    }

    addReport(r: ReportInfoCard) {
        this.setState(
            'groups',
            grp => grp.description === 'My Reports',
            'reports',
            (reports) => [...reports, r]
        )
    }

    removeReport(r: ReportInfoCard) {
        this.setState(
            'groups',
            grp => grp.reports.some(rpt => rpt.reportGU === r.reportGU),
            'reports',
            (reports) => [
                ...reports.slice(0, reports.findIndex(rpt => rpt.reportGU === r.reportGU)),
                ...reports.slice(reports.findIndex(rpt => rpt.reportGU === r.reportGU) + 1),
            ]
        )
    }

    expandReport(r: ReportInfoCard) {
        if (this.selectedGroup()) {
            const reportIndex = this.selectedGroup()!.reports.findIndex(rpt => rpt.reportGU === r.reportGU)
            this.setState('carouselIndex', reportIndex)
            this.setState('isInCarousel', true)
        }
    }

    search = debounce((q: string) => {
        if (!q) {
            this.setState('searchResults', [])
            return
        }

        const reportsToSearch = this.selectedGroup() ? this.selectedGroup()!.reports : this.allReports()

        const results = reportsToSearch.filter(r => {
            return r.number?.toLowerCase().includes(q.toLowerCase()) || r.name?.toLowerCase().includes(q.toLowerCase())
        })

        this.setState('searchResults', results)
    }, 200)


    launchReportInterface(report: ReportInfoCard) {
        const reportGU = report.groupReportGU ? `${report.reportGU}^${report.groupReportGU}` : report.reportGU
        //@ts-ignore
        ST.GetParentInstance().OpenViewInDialog(reportGU, reportGU, {
            'REV_RENDER': {
                'VIEW': {
                    '@TYPE': 'REPORT',
                }
            }
        })
    }

    async launchReportDesigner (report: ReportInfoCard) {
        try {
            //@ts-ignore
            window.ST.OpenViewWindow('2D6E7F8B-E654-4CF6-9116-F851E33046CB', null, report.reportGU);
        } catch (e) {
            toast.notify('error', 'An error ocurred while launching the report')
            console.error(e)
        }
    }

    setCanEdit = (v: boolean) => { this.setState('canEdit', v) }
    setCanAdd = (v: boolean) => { this.setState('canAdd', v) }
    setCanPublish = (v: boolean) => { this.setState('canPublish', v) }
    setCanAccessMyReports = (v: boolean) => { this.setState('canAccessMyReports', v) }

    closeAllDialogs = () => {
        this.setState('isCopyDialogOpen', false)
        this.setState('isThumbnailDialogOpen', false)
        this.setState('isPublishDialogOpen', false)
        this.setState('isDeleteDialogOpen', false)
    }

    openCopyDialog(r: ReportInfoCard) {
        this.closeAllDialogs()
        this.setState('isCopyDialogOpen', true)
        this.setState('selectedReportId', r.reportGU)
    }

    cancelCopy() {
        this.setState('isCopyDialogOpen', false)
        this.setState('selectedReportId', undefined)
    }

    openReportThumbnailDialog(r: ReportInfoCard) {
        this.closeAllDialogs()
        this.setState('isThumbnailDialogOpen', true)
        this.setState('selectedReportId', r.reportGU)
    }

    cancelReportThumbnail() {
        this.setState('isThumbnailDialogOpen', false)
        this.setState('selectedReportId', undefined)
    }

    openPublishDialog(r: ReportInfoCard) {
        this.closeAllDialogs()
        this.setState('isPublishDialogOpen', true)
        this.setState('selectedReportId', r.reportGU)
    }

    cancelPublish() {
        this.setState('isPublishDialogOpen', false)
        this.setState('selectedReportId', undefined)
    }

    openDeleteDialog(r: ReportInfoCard) {
        this.closeAllDialogs()
        this.setState('isDeleteDialogOpen', true)
        this.setState('selectedReportId', r.reportGU)
    }

    cancelDelete() {
        this.setState('isDeleteDialogOpen', false)
        this.setState('selectedReportId', undefined)
    }

    async deleteReport(r: ReportInfoCard) {
        this.setState('isDeleting', true)
        try {
            const success = await api.DeleteReport(r.reportGU!)
            if (success) {
                this.removeReport(r)
                this.setState('isDeleteDialogOpen', false)
                this.refreshPad()
                toast.notify('success', `Successfully delete report ${r.number} - ${r.name}`)
            }
        } catch (e) {
            toast.notify('error', 'An error ocurred while deleting the report.')
            console.error(e)
        }
        this.setState('isDeleting', false)
    }

    async encode(file: File) {
        return new Promise<string>((resolve) => {
            const reader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
                const dataUrl = event.target!.result as string;
                const [_, base64] = dataUrl.split(',');

                resolve(base64);
            };

            reader.readAsDataURL(file);
        });
    }

    async updateReportThumbnail (r: ReportInfoCard, file: File) {
        this.setState('isUpdatingThumbnail', true)
        try {
            const thumbnail = await this.encode(file)
            const extension = file.name.split('.').pop()!
            const data = await api.EditReport(r.reportGU!, '', '', '', thumbnail, extension)

            if (data) {
                this.updateReport(r, data)
            }
        } catch (e) {
            toast.notify('error', 'An error ocurred while updating the report thumbnail')
        }
        this.setState('isUpdatingThumbnail', false)
    }

    async setReportThumbnailFromPreview (r: ReportInfoCard) {
        this.setState('isUpdatingThumbnail', true)

        try {

        } catch (e) { }

        this.setState('isUpdatingThumbnail', false)
    }

    async setReportThumbnail (reportGu: string, img: string, extension: string) {
        this.setState('isUpdatingThumbnail', true)
        try {
            const url = await api.SetReportThumbnail(reportGu, img, extension)
            if (url) {
                this.setState(
                    'groups',
                    (g) => g.reports.some(r => r.reportGU === reportGu),
                    'reports',
                    (r) => r.reportGU === reportGu,
                    'reportPreviewImage',
                    url
                )
            }
        } catch (e) {
            toast.notify('error', 'An error ocurred while updating the report thumbnail')
        }
        this.setState('isUpdatingThumbnail', false)
    }

    async publishReport (r: ReportInfoCard, location: string, publishToPad: boolean, overrideDefault: boolean) {
        this.setState('isPublishing', true)

        try {
            const data = await api.PublishReport(r.reportGU!, location, publishToPad, overrideDefault)
            if (data) {
                this.refreshPad()

                await this.refresh()

                const newReport = this.allReports().find(r => r.reportGU === data.reportGU)
                const group = this.state.groups.find(g => g.reports.some(r => r.reportGU === newReport?.reportGU))

                if (group && newReport) {
                    this.setSelectedGroup(group)
                    this.expandReport(newReport)
                }

                //@ts-ignore
                toast.notify('success', `Report "${newReport?.number} - ${newReport?.name}" has been published to the PAD Tree at "${data?.padPath}"`)

                this.setState('isPublishDialogOpen', false)
            }
        } catch (e) {
            toast.notify('error', 'An error occurred while publishing the report.')
        }

        this.setState('isPublishing', false)
    }

    refreshPad () {
        const $view = window.ST.GetRootWindow().$('.REV_VIEW[data-guid="832C78E7-471A-4392-BE48-684799F3969D"]')
        $view.each(function () {
            //@ts-ignore
            const viewData = window.ST.GetRootWindow().$(this).viewData();
            if (viewData) {
                viewData.RefreshOrReload(true, null);
            }
        })
    }

    setReportBrowserScrollTop (s: number) {
        this.setState('reportBrowserScrollTop', s)
    }

    setReportGroupLoading (v: boolean) {
        this.setState('isLoadingGroups', v)
    }

}

export function ReportBrowserProvider(props: ParentProps) {
    const reportBrowser = new ReportBrowserContext();

    return (
        <Context.Provider value={reportBrowser}>
            {props.children}
        </Context.Provider>
    )
}

export function useReportBrowser() {
    const ctx = useContext(Context)
    if (!ctx) throw new Error('useReportBrowser must be used within a ReportBrowserProvider')
    return ctx
}